import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Flame, Mail, Loader2, ArrowLeft, Smartphone, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import SmsOtpForm from "../components/auth/SmsOtpForm";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center grid-bg px-4 relative">
      <div className="absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full bg-orange-500/10 blur-3xl" />
      <div className="relative w-full max-w-md glass-strong rounded-lg p-8 f360-border-gradient">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg f360-gradient flex items-center justify-center"><Flame className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
          <div>
            <h1 className="text-xl font-extrabold f360-gradient-text">{title}</h1>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>
        {children}
        <Link to="/login" className="text-xs text-orange-600 hover:text-orange-700 mt-6 flex items-center gap-1"><ArrowLeft className="w-3 h-3" strokeWidth={2.5} />Back to sign in</Link>
      </div>
    </div>
  );
}

const errMsg = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);

// Last step (both routes end here): choose a new password with a reset token.
function NewPasswordForm({ token, onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Use at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/tenant/reset-password", { token, password: pw });
      onDone();
    } catch (e2) { toast.error(errMsg(e2, "Reset failed — the link or code may have expired")); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Input required type="password" minLength={8} autoComplete="new-password" placeholder="New password (min. 8 characters)" value={pw} onChange={(e) => setPw(e.target.value)} className="bg-gray-50 border-gray-200 h-12" data-testid="new-password" />
      <Input required type="password" minLength={8} autoComplete="new-password" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="bg-gray-50 border-gray-200 h-12" data-testid="confirm-password" />
      <Button type="submit" disabled={busy} className="w-full h-12 f360-gradient border-0 text-white">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Update Password"}
      </Button>
      <p className="text-[11px] text-gray-500">You'll be signed out of every device and asked to sign in again.</p>
    </form>
  );
}

// SMS code (Firebase): mobile number → code → new password.
function SmsReset({ config }) {
  const nav = useNavigate();
  const [token, setToken] = useState("");
  if (!token) {
    return (
      <SmsOtpForm config={config} verifyLabel="Verify code" purpose="reset"
        onVerified={async (idToken) => setToken((await api.post("/tenant/forgot-password/sms", { id_token: idToken })).data.reset_token)} />
    );
  }
  return (
    <NewPasswordForm token={token} onDone={() => { toast.success("Password updated. Please sign in."); nav("/login"); }} />
  );
}

function EmailReset() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tenant/forgot-password", { email });
      setSent(true);
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  if (sent) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4 text-sm flex gap-2">
        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
        <span>If an account exists for <b>{email}</b>, you'll receive a reset link shortly. It's valid for 1 hour.</span>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="relative">
        <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <Input required type="email" data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="pl-10 bg-gray-50 border-gray-200 h-12" />
      </div>
      <Button type="submit" disabled={busy} className="w-full h-12 f360-gradient border-0 text-white">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Send Reset Link"}
      </Button>
    </form>
  );
}

export function ForgotPassword() {
  const [smsConfig, setSmsConfig] = useState(null);
  const [mode, setMode] = useState("email");
  useEffect(() => {
    api.get("/auth/otp/channels").then((r) => {
      if (r.data.sms) { setSmsConfig(r.data.sms); setMode("sms"); }
    }).catch(() => {});
  }, []);
  const tab = (key, label, Icon) => (
    <button type="button" onClick={() => setMode(key)} data-testid={`reset-mode-${key}`}
      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-semibold transition ${mode === key ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-800"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
  return (
    <Shell title="Reset Password" subtitle={smsConfig ? "Get a code by SMS or a link by email" : "Get a reset link by email"}>
      {smsConfig && (
        <div className="flex gap-1 p-1 mb-4 rounded-lg bg-gray-100">
          {tab("sms", "SMS code", Smartphone)}
          {tab("email", "Email link", Mail)}
        </div>
      )}
      {mode === "sms" && smsConfig ? <SmsReset config={smsConfig} /> : <EmailReset />}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  return (
    <Shell title="Set new password">
      <NewPasswordForm token={params.get("token")} onDone={() => { toast.success("Password updated. Please sign in."); nav("/login"); }} />
    </Shell>
  );
}
